import { Controller } from '@hotwired/stimulus';

// Also read literally in _posthog_snippet.html.slim; see controllers/helpers/extension_presence.js.
const PRESENCE_ATTRIBUTE = 'data-unscreen-extension';
const DRAFT_KEY = 'unscreenFeedbackDraft';
// Long enough to read "Message sent. Thank you!", short enough that nobody has to close the panel.
const DISMISS_DELAY = 3000;

// The form works without this controller — it posts and lands on /feedbacks/thanks. What this adds is
// submitting in place, so nobody loses the page (or, on lite, the tools they had switched on).
export default class extends Controller {
  static targets = ['form', 'textarea', 'charCount', 'submitButton', 'result', 'distinctId', 'extensionVersion'];

  connect() {
    this.#restoreDraft();
    this.updateCharCount();
    this.#stampContext();
    this.submitting = false;
    this.sent = false;
    this.panel = this.element.closest('.offcanvas');
    // Only after a send: closing mid-draft must leave the textarea alone.
    this.resetOnHidden = () => this.sent && this.#reset();
    this.panel?.addEventListener('hidden.bs.offcanvas', this.resetOnHidden);
  }

  disconnect() {
    clearTimeout(this.dismissTimer);
    this.panel?.removeEventListener('hidden.bs.offcanvas', this.resetOnHidden);
  }

  updateCharCount() {
    this.charCountTarget.textContent = this.textareaTarget.value.length.toLocaleString();
  }

  submit(event) {
    if (!this.formTarget.checkValidity()) {
      event.preventDefault();
      this.formTarget.classList.add('was-validated');
      return;
    }

    event.preventDefault();
    if (this.submitting) return;

    this.submitting = true;
    this.#lockButton();
    this.#stampContext();
    this.#send();
  }

  // The token rides in the form body, not a header: lite renders no CSRF meta tag at all (its page is one
  // shared Cloudflare object), and its half of this endpoint is not checked for one.
  async #send() {
    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.formTarget),
      });
      // Not every rejection is ours: the throttle answers 'Retry later' as plain text, and a 500 or a
      // rejected token answers HTML, so parsing has to be allowed to come back empty.
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.message) return this.#showSuccess(payload.message);
      this.#showFailure(payload.error);
    } catch {
      this.#showFailure();
    }
  }

  #showSuccess(message) {
    this.#clearDraft();
    this.sent = true;
    // review_prompt_controller must not ask someone who has just reported a problem.
    this.dispatch('sent');
    this.formTarget.classList.add('d-none');
    this.resultTarget.classList.remove('d-none', 'text-danger');
    this.resultTarget.classList.add('fw-result--sent');
    this.resultTarget.textContent = message;
    this.dismissTimer = setTimeout(() => this.#dismiss(), DISMISS_DELAY);
  }

  // The reset rides on hidden.bs.offcanvas so the form doesn't reappear behind the thank-you on the way
  // out. Inline, the embedding controller handles feedback:dismiss.
  #dismiss() {
    if (!this.panel) return this.dispatch('dismiss');

    const offcanvas = window.bootstrap?.Offcanvas;
    if (!offcanvas) return this.#reset();

    offcanvas.getOrCreateInstance(this.panel).hide();
  }

  // A second visit has to find the form, not the last message.
  #reset() {
    clearTimeout(this.dismissTimer);
    this.sent = false;
    this.submitting = false;
    this.formTarget.reset();
    this.#stampContext();
    this.updateCharCount();
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.value = this.originalButtonLabel;
    this.resultTarget.classList.add('d-none');
    this.resultTarget.classList.remove('fw-result--sent');
    this.resultTarget.textContent = '';
    this.formTarget.classList.remove('d-none', 'was-validated');
  }

  // The message stays in the textarea and the button comes back: a failed send must not cost someone
  // what they wrote.
  #showFailure(message) {
    this.#storeDraft();
    this.submitting = false;
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.value = this.originalButtonLabel;
    this.resultTarget.classList.remove('d-none');
    this.resultTarget.classList.add('text-danger');
    this.resultTarget.textContent = message || 'Unknown error. Please try again';
  }

  #lockButton() {
    this.originalButtonLabel = this.submitButtonTarget.value;
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.value = 'Submitting...';
  }

  // Both are the browser's, not ours: the distinct_id is what joins this submission to that visitor's
  // other events, and the version is what says which build they were describing.
  #stampContext() {
    // Typed: until array.js loads (or if an ad blocker stops it) posthog is still the snippet's stub, whose
    // every method is an Array#push and so answers with a length — a truthy number that would be stored as an id.
    const distinctId = window.posthog?.get_distinct_id?.();
    if (typeof distinctId === 'string' && distinctId) this.distinctIdTarget.value = distinctId;
    this.extensionVersionTarget.value = document.documentElement.getAttribute(PRESENCE_ATTRIBUTE) || '';
  }

  // Storage throws where it's blocked; losing a draft is fine, breaking the widget isn't.
  #storeDraft() {
    try {
      sessionStorage.setItem(DRAFT_KEY, this.textareaTarget.value);
    } catch {
      // See above.
    }
  }

  #restoreDraft() {
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) this.textareaTarget.value = draft;
    } catch {
      // See above.
    }
  }

  #clearDraft() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      // See above.
    }
  }
}
